import { useIsMobile } from "@/hooks/use-mobile";
import { getFolders } from "@/redux/clients/clients.action";
import { useAppDispatch } from "@/redux/hooks";
import { RoutesEnum } from "@/routes/enum/Routes";
import { Typography } from "@mui/material";
import { ColumnDef, Row, Column } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

export type CompaniesColumns = {
  clienteId: number;
  numeroCliente: string;
  clienteNombre: string;
  quantityCarpetas: string;
  canales: string[];
};

const SortableHeader = ({
  column,
  children,
}: {
  column: Column<CompaniesColumns, unknown>;
  children: React.ReactNode;
}) => {
  const sorted = column.getIsSorted();
  return (
    <div
      className="flex items-center gap-2 cursor-pointer hover:text-gray-600 select-none"
      onClick={() => {
        if (sorted === "asc") {
          column.toggleSorting(true); // desc
        } else if (sorted === "desc") {
          column.clearSorting(); // clear
        } else {
          column.toggleSorting(false); // asc
        }
      }}
    >
      {children}
      <div className="flex flex-col">
        {sorted === "asc" && <ChevronUp className="h-4 w-4" />}
        {sorted === "desc" && <ChevronDown className="h-4 w-4" />}
        {!sorted && <ChevronsUpDown className="h-4 w-4" />}
      </div>
    </div>
  );
};

const LinkableLabel = ({
  label,
  clienteId,
}: {
  label: string;
  clienteId: string;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  return (
    <Typography
      fontFamily={"Inter"}
      fontSize={"14px"}
      fontWeight={400}
      className="truncate max-w-[140px] cursor-pointer underline text-[#1F6B45]"
      onClick={() => {
        navigate(RoutesEnum.FOLDERS, {
          state: {
            clienteId,
            clienteNombre: label,
          },
        });
        dispatch(getFolders(clienteId));
      }}
    >
      {label}
    </Typography>
  );
};

export function useCompaniesColumns(): ColumnDef<CompaniesColumns>[] {
  const isMobile = useIsMobile();
  const canalLabels = {
    N: { label: "Naranja", color: "text-orange-500" },
    R: { label: "Rojo", color: "text-red-500" },
    V: { label: "Verde", color: "text-green-500" },
  };
  const columnsCompanies: ColumnDef<CompaniesColumns>[] = [
    {
      accessorKey: "clienteNombre",
      header: ({ column }: { column: Column<CompaniesColumns, unknown> }) => (
        <div className="text-left">
          <SortableHeader column={column}>
            <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
              Cliente
            </Typography>
          </SortableHeader>
        </div>
      ),
      cell: ({ row }: { row: Row<CompaniesColumns> }) => {
        return (
          <div
            className={`text-left font-medium ${
              isMobile ? "max-w-[100px]" : "max-w-[150px]"
            }`}
            aria-label="cliente-nombre"
          >
            <LinkableLabel
              label={row.original.clienteNombre}
              clienteId={row.original.clienteId.toString()}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "numeroCliente",
      header: ({ column }: { column: Column<CompaniesColumns, unknown> }) => (
        <div className="text-left">
          <SortableHeader column={column}>
            <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
              {isMobile ? "Nro. cliente" : "Número de cliente"}
            </Typography>
          </SortableHeader>
        </div>
      ),
      cell: ({ row }: { row: Row<CompaniesColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="numero-cliente"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {row.original.numeroCliente}
            </Typography>
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: "quantityCarpetas",
      header: ({ column }: { column: Column<CompaniesColumns, unknown> }) => (
        <div className="text-left">
          <SortableHeader column={column}>
            <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
              {isMobile ? "Nro. carpetas" : "Cantidad de carpetas"}
            </Typography>
          </SortableHeader>
        </div>
      ),
      sortingFn: (
        rowA: Row<CompaniesColumns>,
        rowB: Row<CompaniesColumns>,
        columnId: string,
      ) => {
        const numA = parseInt(rowA.getValue(columnId) as string) || 0;
        const numB = parseInt(rowB.getValue(columnId) as string) || 0;
        return numA - numB;
      },
      cell: ({ row }: { row: Row<CompaniesColumns> }) => {
        return (
          <div
            className="text-left font-medium max-w-[150px]"
            aria-label="cantidad-carpetas"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate"
            >
              {row.original.quantityCarpetas}
            </Typography>
          </div>
        );
      },
    },
    {
      accessorKey: "canales",
      header: () => (
        <div className={`text-left ${isMobile ? "hidden" : ""}`}>
          <Typography fontFamily={"Inter"} fontSize={"14px"} fontWeight={500}>
            Canales
          </Typography>
        </div>
      ),
      cell: ({ row }: { row: Row<CompaniesColumns> }) => {
        return (
          <div
            className={`text-left font-medium max-w-[150px] ${
              isMobile ? "hidden" : ""
            }`}
            aria-label="canales"
          >
            <Typography
              fontFamily={"Inter"}
              fontSize={"14px"}
              fontWeight={400}
              className="truncate min-w-[140px]"
            >
              {row.original.canales ? (
                row.original.canales.map((canal, index) => {
                  const { label, color } =
                    canalLabels[canal as keyof typeof canalLabels];
                  return (
                    <span key={index} className={`${color}`}>
                      {label}
                      {index < row.original.canales.length - 1 && (
                        <span className="text-black">, </span>
                      )}
                    </span>
                  );
                })
              ) : (
                <span className="text-gray-500">- - -</span>
              )}
            </Typography>
          </div>
        );
      },
    },
  ].filter(Boolean) as ColumnDef<CompaniesColumns>[];
  return columnsCompanies;
}
